<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex flex-wrap align-center">
        <h3 class="mr-4">Syndication Partners</h3>
        <v-btn
          class="ml-2"
          color="success"
          :disabled="loading || saving"
          @click="openCreateDialog"
        >
          Create
        </v-btn>
      </v-card-title>

      <v-card-text>
        <Loader v-if="loading" />

        <template v-else>
          <div v-if="loadError" class="mb-4">
            <v-alert type="error" dismissible @click:close="loadError = ''">
              {{ loadError }}
            </v-alert>
          </div>

          <div v-if="feedsLoadError" class="mb-4">
            <v-alert type="warning" dense outlined dismissible @click:close="feedsLoadError = ''">
              {{ feedsLoadError }}
            </v-alert>
          </div>

          <v-text-field
            v-model="search"
            label="Global Search"
            prepend-icon="mdi-magnify"
            clearable
            class="mb-4"
          />

          <v-data-table
            class="syndication-clients-table"
            :headers="headers"
            :items="tableItems"
            :search="search"
            item-key="_listIndex"
            sort-by="_rowNum"
            :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
          >
            <template
              v-for="col in columnKeys"
              v-slot:[`item.${col}`]="{ item }"
            >
              <v-checkbox
                v-if="displayAsCheckbox(col, item[col])"
                :key="`${col}-bool`"
                class="syndication-bool-cell mt-0 pt-0"
                :input-value="cellBooleanValue(col, item)"
                disabled
                hide-details
                dense
              />
              <span
                v-else
                :key="`${col}-text`"
                class="d-inline-block"
                :class="
                  isDomainColumn(col)
                    ? 'cell-domain'
                    : isFeedColumn(col)
                      ? 'cell-feed'
                      : 'cell-truncate'
                "
                :title="cellTitle(item[col])"
              >
                {{ formatCell(item[col], col) }}
              </span>
            </template>

            <template v-slot:item._customUiPresent="{ item }">
              <div class="d-inline-flex align-center justify-center syndication-customui-cell flex-nowrap">
                <v-tooltip v-if="item._customUiPresent" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="deep-purple darken-1"
                      class="syndication-customui-icon mr-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-puzzle-outline
                    </v-icon>
                  </template>
                  <span>Has custom UI</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="syndication-customui-edit"
                      color="deep-purple darken-1"
                      :disabled="saving"
                      exact
                      v-bind="attrs"
                      v-on="on"
                      :to="{
                        name: 'syndication-partner-custom-ui',
                        params: { partnerIndex: String(item._listIndex) },
                      }"
                      data-testid="syndication-edit-customui"
                    >
                      <v-icon small>mdi-file-document-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit custom UI</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-icon
                class="mr-2"
                color="primary"
                :disabled="saving"
                @click="editItem(item)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                color="warning"
                :disabled="saving"
                @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="editDialog"
      max-width="960"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
      persistent
    >
      <v-card class="syndication-partner-dialog-card">
        <v-card-title>{{ editIndex === null ? 'New partner' : 'Edit partner' }}</v-card-title>
        <v-card-text class="pt-4">
          <v-alert v-if="formError" type="error" dense text class="mb-4">
            {{ formError }}
          </v-alert>

          <v-row dense>
            <v-col
              v-for="key in editOrderedKeys"
              :key="key"
              cols="12"
              :sm="fieldType(key) === 'boolean' ? 6 : 12"
              :md="fieldType(key) === 'boolean' ? 4 : 12"
              :lg="fieldType(key) === 'boolean' ? 3 : 12"
            >
              <v-switch
                v-if="fieldType(key) === 'boolean'"
                v-model="editDraft[key]"
                :label="fieldLabel(key)"
                :disabled="saving || isBooleanFieldDisabled(key)"
                :data-testid="switchTestId(key)"
                inset
                hide-details="auto"
                class="mt-0"
              />

              <v-text-field
                v-else-if="fieldType(key) === 'number'"
                v-model="editDraft[key]"
                type="number"
                :label="fieldLabel(key)"
                outlined
                dense
                hide-details="auto"
              />

              <v-textarea
                v-else-if="fieldType(key) === 'json'"
                v-model="editJsonFieldText[key]"
                :label="fieldLabel(key)"
                outlined
                dense
                auto-grow
                rows="4"
                hide-details="auto"
                class="mono-json"
                spellcheck="false"
              />

              <v-textarea
                v-else-if="fieldType(key) === 'notes'"
                v-model="editDraft[key]"
                :label="fieldLabel(key)"
                outlined
                dense
                auto-grow
                rows="3"
                hide-details="auto"
              />

              <template v-else-if="fieldType(key) === 'feed'">
                <v-autocomplete
                  v-if="feeds.length"
                  v-model="editDraft[key]"
                  :items="feeds"
                  item-text="name"
                  item-value="id"
                  :label="fieldLabel(key)"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :loading="feedsLoading"
                  :menu-props="{ maxHeight: 320 }"
                  data-testid="syndication-feed-select"
                >
                  <template v-slot:selection="slot">
                    <span>{{ feedSelectionLabel(slot.item, editDraft[key]) }}</span>
                  </template>
                  <template v-slot:item="slot">{{ feedSelectionLabel(slot.item, slot.item && slot.item.id) }}</template>
                </v-autocomplete>
                <v-text-field
                  v-else
                  v-model="editDraft[key]"
                  type="number"
                  :label="fieldLabel(key)"
                  outlined
                  dense
                  hide-details="auto"
                  hint="Feeds list unavailable — enter numeric feed ID"
                  persistent-hint
                  data-testid="syndication-feed-id-input"
                />
              </template>

              <v-select
                v-else-if="fieldType(key) === 'locale'"
                v-model="editDraft[key]"
                :items="locales"
                item-text="text"
                item-value="value"
                :label="fieldLabel(key)"
                outlined
                dense
                hide-details="auto"
                :menu-props="{ maxHeight: 320 }"
                data-testid="syndication-default-locale-select"
              />

              <v-text-field
                v-else
                v-model="editDraft[key]"
                :label="fieldLabel(key)"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="closeEditDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="applyEditDialog"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import Loader from '../Loader/Loader.vue';
import config from '../../config';
import { ClearCacheService } from '../../services/ClearCacheService';
import persistentDialogEsc from '@/mixins/persistentDialogEsc';
import {
  normalizeSyndicationClientsBody,
  ensurePartnerDefaults,
  resolveCtaEnabledValue,
  isPrerollEnabled,
  isCtaSwitchLocked,
  isCtaFieldKey,
  syncCtaEnabledWithPreroll,
  normalizePartnerCtaForSave,
} from './syndicationNormalize';

const MAX_AUTO_COLUMNS = 14;

/** Preferred order of client properties in the edit form (camelCase; matched case-insensitively to API keys). */
const FORM_FIELD_ORDER = [
  'domain',
  'defaultLocale',
  'feedId',
  'ctaEnabled',
  'prerollEnabled',
  'rewardAdEnabled',
  'postrollEnabled',
  'ctaDisplayAdEnabled',
  'endDisplayAdEnabled',
  'arkadiumCanonicalEnabled',
  'notes',
];

/**
 * Omitted from the edit/create dialog (pass-through fields).
 * When saving an existing partner, values are merged from the previous row if absent from the form output.
 */
const FORM_HIDDEN_FROM_DIALOG = ['rewardurl', 'rewardtype', 'customui'];

/** Present in the form but not rendered as a data-table column. */
const TABLE_HIDDEN_COLUMNS = ['notes', 'customui'];

function isFormHiddenFromDialog(key) {
  return FORM_HIDDEN_FROM_DIALOG.includes(String(key).toLowerCase());
}

function isTableHiddenColumn(key) {
  return TABLE_HIDDEN_COLUMNS.includes(String(key).toLowerCase());
}

function isNonEmptyPlainObject(value) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.keys(value).length > 0;
}

/** True if partner JSON has a `customUi` property (any casing) with a non-empty object. */
function clientHasNonEmptyCustomUi(client) {
  if (!client || typeof client !== 'object' || Array.isArray(client)) {
    return false;
  }
  const key = Object.keys(client).find((k) => k.toLowerCase() === 'customui');
  if (!key) {
    return false;
  }
  return isNonEmptyPlainObject(client[key]);
}

/** Map JSON property name (any casing) -> table header label */
const COLUMN_HEADER_LABELS = {
  domain: 'Domain',
  defaultlocale: 'Default Locale',
  feedid: 'Feed',
  ctaenabled: 'CTA',
  prerollenabled: 'Preroll Video Ad',
  rewardadenabled: 'Reward Video Ad',
  rewardenabled: 'Reward Video Ad',
  postrollenabled: 'Postroll Video Ad',
  enddisplayadenabled: 'Game End Display Ad',
  ctadisplayadenabled: 'Game Start Display Ad',
  arkadiumcanonicalenabled: 'Arkadium.com Canonical',
  rewardurl: 'Reward URL',
  rewardtype: 'Reward Type',
  notes: 'Notes',
};

/** Map JSON property name (any casing) -> form field label */
const FORM_FIELD_LABELS = {
  arkadiumcanonicalenabled: 'Use Arkadium.com Domain in canonical url',
};

function columnHeaderText(key) {
  if (key == null || key === '') {
    return key;
  }
  const normalized = String(key).toLowerCase();
  return COLUMN_HEADER_LABELS[normalized] || key;
}

/** Sort keys by `FORM_FIELD_ORDER`, then alphabetically for unknown keys. */
function orderKeysByFormFieldOrder(keys) {
  if (!keys || !keys.length) {
    return keys || [];
  }
  const lower = (k) => String(k).toLowerCase();
  const orderMap = new Map(FORM_FIELD_ORDER.map((k, i) => [lower(k), i]));
  return [...keys].sort((a, b) => {
    const ai = orderMap.has(lower(a)) ? orderMap.get(lower(a)) : 1000;
    const bi = orderMap.has(lower(b)) ? orderMap.get(lower(b)) : 1000;
    if (ai !== bi) {
      return ai - bi;
    }
    return String(a).localeCompare(String(b));
  });
}

/** Place `postrollEnabled` immediately after the reward column (`rewardAdEnabled` or `rewardEnabled`). */
function orderKeysPostrollAfterReward(keys) {
  if (!keys || !keys.length) {
    return keys || [];
  }
  const lower = (k) => String(k).toLowerCase();
  const postrollIdx = keys.findIndex((k) => lower(k) === 'postrollenabled');
  if (postrollIdx < 0) {
    return keys.slice();
  }
  const rewardIdx = keys.findIndex((k) => {
    const l = lower(k);
    return l === 'rewardadenabled' || l === 'rewardenabled';
  });
  if (rewardIdx < 0) {
    return keys.slice();
  }
  const postrollKey = keys[postrollIdx];
  const withoutPostroll = keys.filter((_, i) => i !== postrollIdx);
  const rewardIdx2 = withoutPostroll.findIndex((k) => {
    const l = lower(k);
    return l === 'rewardadenabled' || l === 'rewardenabled';
  });
  if (rewardIdx2 < 0) {
    return keys.slice();
  }
  const next = [...withoutPostroll];
  next.splice(rewardIdx2 + 1, 0, postrollKey);
  return next;
}

function resolveKeyInObject(obj, logicalKey) {
  if (!obj || typeof obj !== 'object') {
    return null;
  }
  const t = String(logicalKey).toLowerCase();
  const found = Object.keys(obj).find((k) => k.toLowerCase() === t);
  return found || null;
}

/** Game API may use camelCase or PascalCase; normalize for v-autocomplete item-text / slots. */
function normalizeFeedListItem(f) {
  if (!f || typeof f !== 'object') {
    return f;
  }
  const id = f.id != null ? Number(f.id) : f.id;
  const name = f.name != null ? f.name : (f.Name != null ? f.Name : '');
  const environment =
    f.environment != null ? f.environment : (f.Environment != null ? f.Environment : '');
  return { ...f, id, name, environment };
}

function formatDefaultLocaleCell(value) {
  return value == null || String(value).trim() === '' ? 'en' : String(value);
}

function formatFeedIdCell(value, feedLabelById) {
  if (value == null || value === '') {
    return '';
  }
  const n = Number(value);
  if (!Number.isNaN(n) && feedLabelById && feedLabelById.has(n)) {
    return feedLabelById.get(n);
  }
  return String(value);
}

function buildPartnerDraftOrError({
  editOrderedKeys,
  editDraft,
  editJsonFieldText,
  fieldType,
  fieldLabel,
}) {
  const out = {};

  for (let i = 0; i < editOrderedKeys.length; i += 1) {
    const key = editOrderedKeys[i];
    const t = fieldType(key);

    if (t === 'json') {
      const rawStr = editJsonFieldText[key] != null ? String(editJsonFieldText[key]).trim() : '';
      try {
        out[key] = rawStr === '' ? null : JSON.parse(rawStr);
      } catch (e) {
        return { error: `Invalid JSON in "${fieldLabel(key)}"` };
      }
      continue;
    }

    if (t === 'number') {
      const v = editDraft[key];
      const empty = v === '' || v == null || (typeof v === 'string' && v.trim() === '');
      if (empty) {
        out[key] = null;
        continue;
      }
      const n = Number(v);
      if (Number.isNaN(n)) {
        return { error: `Invalid number in "${fieldLabel(key)}"` };
      }
      out[key] = n;
      continue;
    }

    if (t === 'boolean') {
      out[key] = !!editDraft[key];
      continue;
    }

    if (t === 'feed') {
      const v = editDraft[key];
      if (v === '' || v == null) {
        out[key] = null;
        continue;
      }
      const n = Number(v);
      if (Number.isNaN(n)) {
        return { error: `Invalid "${fieldLabel(key)}" — use a numeric feed ID` };
      }
      out[key] = n;
      continue;
    }

    if (t === 'locale') {
      const v = editDraft[key];
      const s = v == null ? '' : String(v).trim().toLowerCase();
      out[key] = s === '' ? 'en' : s;
      continue;
    }

    out[key] = editDraft[key];
  }

  return { out };
}

function validateDomainOrError(out) {
  const domainOutKey = Object.keys(out).find((k) => k.toLowerCase() === 'domain');
  const domainRaw = domainOutKey != null ? out[domainOutKey] : null;
  const domainTrimmed = domainRaw == null ? '' : String(domainRaw).trim();
  return domainTrimmed === '' ? 'Domain cannot be empty' : null;
}

export default {
  name: 'SyndicationConfigPage',
  components: { BasicTemplate, Loader },
  mixins: [
    persistentDialogEsc({
      editDialog: { close: 'closeEditDialog', busy: 'saving' },
    }),
  ],
  data() {
    return {
      loading: false,
      loadError: '',
      clients: [],
      search: '',
      editDialog: false,
      editIndex: null,
      editDraft: {},
      editJsonFieldText: {},
      formError: '',
      saving: false,
      feeds: [],
      feedsLoading: false,
      feedsLoadError: '',
      locales: [
        { text: 'English (en)', value: 'en' },
        { text: 'French (fr)', value: 'fr' },
        { text: 'Italian (it)', value: 'it' },
        { text: 'German (de)', value: 'de' },
        { text: 'Spanish (es)', value: 'es' },
      ],
    };
  },
  computed: {
    feedLabelById() {
      const m = new Map();
      (this.feeds || []).forEach((f) => {
        if (f && f.id != null) {
          const id = Number(f.id);
          const env = f.environment != null ? String(f.environment) : '';
          const name = f.name != null ? String(f.name) : '';
          m.set(id, env ? `${name} (${env})` : name);
        }
      });
      return m;
    },
    columnKeys() {
      const keys = new Set();
      this.clients.forEach((c) => {
        if (c && typeof c === 'object' && !Array.isArray(c)) {
          Object.keys(c).forEach((k) => keys.add(k));
        }
      });
      const sorted = [...keys]
        .filter((k) => !isTableHiddenColumn(k))
        .sort((a, b) => a.localeCompare(b));
      return sorted.slice(0, MAX_AUTO_COLUMNS);
    },
    headers() {
      const keys = this.columnKeys;
      const pinLastLower = [
        'ctadisplayadenabled',
        'enddisplayadenabled',
        'arkadiumcanonicalenabled',
        'defaultlocale',
      ];
      const pinned = pinLastLower
        .map((low) => keys.find((k) => String(k).toLowerCase() === low))
        .filter(Boolean);
      const pinnedSet = new Set(pinned.map((k) => k.toLowerCase()));
      const orderedKeys = orderKeysPostrollAfterReward([
        ...orderKeysByFormFieldOrder(keys.filter((k) => !pinnedSet.has(String(k).toLowerCase()))),
        ...pinned,
      ]);

      const cols = orderedKeys.map((k) => {
        const h = { text: columnHeaderText(k), value: k };
        const kl = k && String(k).toLowerCase();
        if (kl === 'domain') {
          h.width = '240px';
          h.class = 'syndication-col-domain';
          h.cellClass = 'syndication-col-domain';
        }
        if (kl === 'feedid') {
          h.width = '220px';
          h.class = 'syndication-col-feed';
          h.cellClass = 'syndication-col-feed';
        }
        return h;
      });
      return [
        {
          text: '#',
          value: '_rowNum',
          width: '30px',
          class: 'syndication-col-num',
          cellClass: 'syndication-col-num',
        },
        ...cols,
        {
          text: 'Custom UI',
          value: '_customUiPresent',
          sortable: true,
          width: '120px',
          align: 'center',
          class: 'syndication-col-customui-marker',
          cellClass: 'syndication-col-customui-marker',
        },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px', align: 'center' },
      ];
    },
    tableItems() {
      return this.clients.map((client, index) => {
        const row = {
          _rowNum: index + 1,
          _listIndex: index,
          _customUiPresent: false,
        };
        if (client && typeof client === 'object' && !Array.isArray(client)) {
          Object.assign(row, client);
          row._customUiPresent = clientHasNonEmptyCustomUi(client);
        }
        return row;
      });
    },
    editOrderedKeys() {
      const draft = this.editDraft || {};
      const usedLower = new Set();
      const keys = [];

      FORM_FIELD_ORDER.forEach((logical) => {
        const existing = resolveKeyInObject(draft, logical);
        const key = existing || logical;
        keys.push(key);
        usedLower.add(key.toLowerCase());
      });

      Object.keys(draft)
        .sort((a, b) => a.localeCompare(b))
        .forEach((k) => {
          if (isFormHiddenFromDialog(k)) {
            return;
          }
          if (!usedLower.has(k.toLowerCase())) {
            keys.push(k);
            usedLower.add(k.toLowerCase());
          }
        });

      return keys;
    },
    prerollEnabledInDraft() {
      return isPrerollEnabled(this.editDraft || {});
    },
  },
  watch: {
    prerollEnabledInDraft(isOn) {
      if (!this.editDialog || !isOn) {
        return;
      }
      const synced = syncCtaEnabledWithPreroll(this.editDraft);
      const ctaKey = resolveKeyInObject(this.editDraft, 'ctaEnabled') || 'ctaEnabled';
      if (synced[ctaKey] !== this.editDraft[ctaKey]) {
        this.$set(this.editDraft, ctaKey, synced[ctaKey]);
      }
    },
  },
  created() {
    this.loading = true;
    Promise.allSettled([this.loadConfigData(), this.loadFeedsData()]).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    httpErrorMessage(err) {
      if (err && err.body) {
        if (typeof err.body === 'object' && err.body.message) {
          return String(err.body.message);
        }
        if (typeof err.body === 'string' && err.body.trim()) {
          return err.body.trim();
        }
      }
      return (err && err.statusText) || 'Request failed';
    },
    persistSyndicationConfig(nextClients) {
      const url = `${config.apiUrl}/SyndicationConfig`;
      return this.$http.post(url, nextClients, {
        headers: { 'Content-Type': 'application/json' },
      });
    },
    /** Used for save and delete: POST first, then CDN purge (same as after save). */
    postSyndicationThenClearCdn(nextClients) {
      return this.persistSyndicationConfig(nextClients).then(() =>
        ClearCacheService.clearSyndicationClientConfigCache(),
      );
    },
    fieldLabel(key) {
      if (key == null || key === '') {
        return key;
      }
      const normalized = String(key).toLowerCase();
      return FORM_FIELD_LABELS[normalized] || columnHeaderText(key);
    },
    switchTestId(key) {
      const kl = String(key).toLowerCase();
      if (kl === 'ctaenabled') {
        return 'syndication-cta-switch';
      }
      if (kl === 'prerollenabled') {
        return 'syndication-preroll-switch';
      }
      return undefined;
    },
    isBooleanFieldDisabled(key) {
      return isCtaFieldKey(key) && isCtaSwitchLocked(this.editDraft);
    },
    fieldType(key) {
      const v = this.editDraft[key];
      const kl = String(key).toLowerCase();
      // Must run before the generic number check: numeric feedId is still the feed picker, not a raw number field.
      if (kl === 'feedid') {
        return 'feed';
      }
      if (kl === 'defaultlocale') {
        return 'locale';
      }
      if (typeof v === 'boolean') {
        return 'boolean';
      }
      if (typeof v === 'number' && !Number.isNaN(v)) {
        return 'number';
      }
      if (v !== null && typeof v === 'object') {
        return 'json';
      }
      if (kl === 'notes') {
        return 'notes';
      }
      if (
        kl === 'enddisplayadenabled'
        || kl === 'prerollenabled'
        || kl === 'rewardenabled'
        || kl === 'rewardadenabled'
        || (kl.endsWith('enabled') && kl !== 'disabled')
      ) {
        return 'boolean';
      }
      if (
        kl === 'count'
        || kl.endsWith('count')
        || kl.endsWith('amount')
        || kl.endsWith('rate')
      ) {
        return 'number';
      }
      return 'string';
    },
    defaultValueForNewField(logicalKey) {
      const kl = String(logicalKey).toLowerCase();
      if (kl === 'defaultlocale') {
        return 'en';
      }
      if (
        kl === 'enddisplayadenabled'
        || kl === 'prerollenabled'
        || kl === 'rewardenabled'
        || kl === 'rewardadenabled'
        || (kl.endsWith('enabled') && kl !== 'disabled')
      ) {
        return true;
      }
      if (kl === 'feedid') {
        return null;
      }
      if (
        kl === 'count'
        || kl.endsWith('count')
        || kl.endsWith('amount')
        || kl.endsWith('rate')
      ) {
        return '';
      }
      return '';
    },
    hydrateJsonFieldText() {
      const next = {};
      this.editOrderedKeys.forEach((key) => {
        if (this.fieldType(key) === 'json') {
          const v = this.editDraft[key];
          next[key] = JSON.stringify(v != null ? v : {}, null, 2);
        }
      });
      this.editJsonFieldText = next;
    },
    isDomainColumn(key) {
      return key && String(key).toLowerCase() === 'domain';
    },
    isFeedColumn(key) {
      return key && String(key).toLowerCase() === 'feedid';
    },
    coerceToBoolean(value) {
      if (value === true || value === 'true' || value === 1 || value === '1') {
        return true;
      }
      return false;
    },
    cellBooleanValue(col, item) {
      const kl = col && String(col).toLowerCase();
      if (kl === 'ctaenabled' && item && typeof item === 'object') {
        return resolveCtaEnabledValue(item);
      }
      return this.coerceToBoolean(item[col]);
    },
    displayAsCheckbox(col, value) {
      const kl = String(col).toLowerCase();
      if (kl === 'ctaenabled') {
        return true;
      }
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === 'boolean') {
        return true;
      }
      if (value === 'true' || value === 'false') {
        return true;
      }
      if (
        kl === 'prerollenabled'
        || kl === 'rewardenabled'
        || kl === 'rewardadenabled'
        || kl === 'enddisplayadenabled'
        || (kl.endsWith('enabled') && kl !== 'disabled')
      ) {
        return value === 0 || value === 1 || value === '0' || value === '1';
      }
      return false;
    },
    normalizeResponseBody(body) {
      return normalizeSyndicationClientsBody(body);
    },
    formatCell(value, columnKey) {
      const col = columnKey && String(columnKey).toLowerCase();
      if (col === 'defaultlocale') {
        return formatDefaultLocaleCell(value);
      }
      if (col === 'feedid') {
        return formatFeedIdCell(value, this.feedLabelById);
      }
      if (value == null) {
        return '';
      }
      if (typeof value === 'object') {
        const s = JSON.stringify(value);
        return s.length > 80 ? `${s.slice(0, 77)}…` : s;
      }
      const s = String(value);
      let maxLen = 120;
      if (col === 'domain') {
        maxLen = 200;
      }
      return s.length > maxLen ? `${s.slice(0, maxLen - 3)}…` : s;
    },
    cellTitle(value) {
      if (value == null) {
        return '';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    },
    async loadConfigData() {
      this.loadError = '';
      if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
        try {
          await this.$msal.ensureAuthenticated();
        } catch (e) {
          console.warn('ensureAuthenticated before syndication config load:', e);
        }
      }
      const url = `${config.apiUrl}/SyndicationConfig`;
      return this.$http
        .get(url)
        .then((res) => {
          this.clients = this.normalizeResponseBody(res.body);
        })
        .catch((err) => {
          console.error(err);
          const code = err.status;
          let msg =
            (err.body && err.body.message) ||
            err.statusText ||
            'Failed to load syndication config.';
          if (code === 401) {
            msg =
              'Not authorized — Azure session may have expired. Sign in again or refresh the page.';
          }
          this.loadError =
            code != null && code !== '' ? `${msg} (HTTP ${code})` : msg;
        });
    },
    feedSelectionLabel(item, idOrHint) {
      if (item && typeof item === 'object') {
        const name = item.name != null ? String(item.name) : '';
        const env = item.environment != null ? String(item.environment) : '';
        if (name) {
          return env ? `${name} (${env})` : name;
        }
      }
      const raw = idOrHint != null ? idOrHint : (item && typeof item === 'object' ? item.id : item);
      if (raw === '' || raw == null) {
        return '';
      }
      const n = Number(raw);
      if (!Number.isNaN(n) && this.feedLabelById.has(n)) {
        return this.feedLabelById.get(n);
      }
      return String(raw);
    },
    loadFeedsData() {
      this.feedsLoadError = '';
      this.feedsLoading = true;
      return this.$gameApi
        .get('/feeds')
        .then((list) => {
          const raw = Array.isArray(list) ? list : [];
          this.feeds = raw.map(normalizeFeedListItem);
        })
        .catch((err) => {
          console.error(err);
          this.feedsLoadError =
            'Could not load feeds list. You can still enter a feed ID manually in the partner dialog.';
          this.feeds = [];
        })
        .finally(() => {
          this.feedsLoading = false;
        });
    },
    editItem(item) {
      const idx = item._listIndex;
      const raw = this.clients[idx];
      this.editIndex = idx;
      this.formError = '';
      this.editDraft = ensurePartnerDefaults(structuredClone(raw == null ? {} : raw));
      const localeKey = resolveKeyInObject(this.editDraft, 'defaultLocale') || 'defaultLocale';
      const localeVal = this.editDraft[localeKey];
      if (!(localeKey in this.editDraft) || localeVal == null || String(localeVal).trim() === '') {
        this.$set(this.editDraft, localeKey, 'en');
      }
      const feedKey = resolveKeyInObject(this.editDraft, 'feedId') || 'feedId';
      if (!(feedKey in this.editDraft)) {
        this.$set(this.editDraft, feedKey, null);
      } else {
        const fv = this.editDraft[feedKey];
        if (fv === '' || fv == null) {
          this.$set(this.editDraft, feedKey, null);
        } else {
          const n = Number(fv);
          if (!Number.isNaN(n)) {
            this.$set(this.editDraft, feedKey, n);
          }
        }
      }
      this.hydrateJsonFieldText();
      this.editDialog = true;
    },
    openCreateDialog() {
      this.editIndex = null;
      this.formError = '';
      this.editDraft = {};
      FORM_FIELD_ORDER.forEach((logical) => {
        const v = this.defaultValueForNewField(logical);
        this.$set(this.editDraft, logical, v);
      });
      this.hydrateJsonFieldText();
      this.editDialog = true;
    },
    closeEditDialog() {
      if (this.saving) {
        return;
      }
      this.editDialog = false;
      this.formError = '';
      this.editJsonFieldText = {};
    },
    applyEditDialog() {
      this.formError = '';
      const built = buildPartnerDraftOrError({
        editOrderedKeys: this.editOrderedKeys,
        editDraft: this.editDraft,
        editJsonFieldText: this.editJsonFieldText,
        fieldType: (k) => this.fieldType(k),
        fieldLabel: (k) => this.fieldLabel(k),
      });
      if (built.error) {
        this.formError = built.error;
        return;
      }
      const out = normalizePartnerCtaForSave(built.out);

      const domainError = validateDomainOrError(out);
      if (domainError) {
        this.formError = domainError;
        return;
      }

      if (this.editIndex !== null) {
        const prev = this.clients[this.editIndex];
        Object.keys(prev).forEach((k) => {
          if (!isFormHiddenFromDialog(k)) {
            return;
          }
          if (!(k in out)) {
            out[k] = prev[k];
          }
        });
      }

      let nextClients;
      if (this.editIndex === null) {
        nextClients = [...this.clients, out];
      } else {
        nextClients = this.clients.map((c, i) => (i === this.editIndex ? out : c));
      }

      this.saving = true;
      this.postSyndicationThenClearCdn(nextClients)
        .then(() => {
          this.$set(this, 'clients', nextClients);
          this.editDialog = false;
          this.editJsonFieldText = {};
          this.$swal.fire({
            icon: 'success',
            title: 'Saved',
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          console.error(err);
          this.$swal.fire({
            icon: 'error',
            title: 'Save failed',
            text: this.httpErrorMessage(err),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async deleteItem(item) {
      const idx = item._listIndex;
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete this partner?',
        showCancelButton: true,
      });
      if (!isConfirmed) {
        return;
      }
      const nextClients = this.clients.filter((_, i) => i !== idx);
      this.saving = true;
      this.postSyndicationThenClearCdn(nextClients)
        .then(() => {
          this.$set(this, 'clients', nextClients);
          this.$swal.fire({
            icon: 'success',
            title: 'Deleted',
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          console.error(err);
          this.$swal.fire({
            icon: 'error',
            title: 'Delete failed',
            text: this.httpErrorMessage(err),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.syndication-partner-dialog-card {
  height: 80vh;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.syndication-partner-dialog-card ::v-deep(.v-card__text) {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
.mono-json :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 13px;
}
.cell-truncate {
  max-width: 220px;
  vertical-align: middle;
}
.cell-domain {
  max-width: 240px;
  min-width: 160px;
  vertical-align: middle;
}
.cell-feed {
  max-width: 220px;
  min-width: 140px;
  vertical-align: middle;
}
::v-deep .syndication-clients-table th.syndication-col-feed,
::v-deep .syndication-clients-table td.syndication-col-feed {
  min-width: 140px;
  max-width: 220px;
}
::v-deep .syndication-clients-table th.syndication-col-domain,
::v-deep .syndication-clients-table td.syndication-col-domain {
  min-width: 160px;
  max-width: 240px;
}
::v-deep .syndication-clients-table th.syndication-col-num,
::v-deep .syndication-clients-table td.syndication-col-num {
  min-width: 30px;
  width: 30px;
}
::v-deep .syndication-clients-table .v-data-table-header th {
  vertical-align: middle;
}
::v-deep .syndication-clients-table .v-data-table-header th .v-data-table-header__text {
  white-space: nowrap;
  line-height: 1.2;
}
.syndication-bool-cell :deep(.v-input--selection-controls__input) {
  margin-right: 0;
}
.syndication-bool-cell :deep(.v-messages) {
  min-height: 0;
}
</style>
